import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import { PanelLeft, PanelRight } from 'lucide-react';
import EditorTopBar from './EditorTopBar';
import type { EditorTab } from './EditorTopBar';
import SlidePanel from './SlidePanel';
import AddFieldModal from './AddFieldModal';
import { SlidePreview } from './SlidePreview';
import SlideToolbar from './SlideToolbar';
import { SlidePropertiesPanel } from './SlidePropertiesPanel';
import { useSlideEditor } from '../../../hooks/useSlideEditor';
import { Modal } from '../../ui/Modal';
import { Tabs } from '../../ui/Tabs';
import { Input } from '../../ui/Input';
import { Select } from '../../ui/Select';
import { Checkbox } from '../../ui/Checkbox';
import { ThemeSettings } from '../ThemeSettings';
import { FlowEditor } from '../FlowEditor';
import { MarkdownImporter } from '../MarkdownImporter';
import { ScoringSettings } from '../ScoringSettings';
import { FormWebhooks } from '../FormWebhooks';
import { FormShare } from '../FormShare';
import { FormResponses } from '../FormResponses';
import { useUpgradeGate } from '../../billing/UpgradeGate';

export const DesktopBuilder: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const editor = useSlideEditor(id);
  const { ensureFeature } = useUpgradeGate();

  // Tab & modal state
  const [activeTab, setActiveTab] = useState<EditorTab>('content');
  const [showMarkdownImporter, setShowMarkdownImporter] = useState(false);

  // Drawer state — only relevant below xl (panels are static columns at >=1280px)
  const [openPanel, setOpenPanel] = useState<'slides' | 'props' | null>(null);

  useEffect(() => {
    if (!openPanel) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenPanel(null);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [openPanel]);

  // Derived
  const localDisplayMode = (editor.settings?.displayMode as 'classic' | 'typeform') || 'typeform';

  // Loading / error states
  if (editor.loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-neutral-950">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
      </div>
    );
  }

  if (editor.error) {
    return (
      <div className="h-screen flex items-center justify-center bg-neutral-950">
        <div className="text-center space-y-3">
          <p className="text-red-400">{editor.error}</p>
          <button
            onClick={editor.exit}
            className="px-4 py-2 rounded-lg bg-white/10 text-white hover:bg-white/15 transition-colors"
          >
            Voltar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-neutral-950">
      {/* Top bar */}
      <EditorTopBar
        title={editor.title}
        onTitleChange={editor.setTitle}
        formId={id}
        slug={editor.formSnapshot.slug}
        username={editor.user?.username}
        isPublished={editor.isPublished}
        hasChanges={editor.hasChanges}
        onSave={editor.save}
        onPublish={editor.publish}
        onBack={editor.exit}
        onAddField={editor.openAddFieldModal}
        activeTab={activeTab}
        onTabChange={(tab) => { editor.closeAddFieldModal(); setActiveTab(tab); }}
        user={editor.user}
        isSaving={editor.isSaving}
      />

      {/* Main content — switches based on active tab */}
      <div className="flex-1 flex min-h-0 relative">
        {activeTab === 'content' && (
          <>
            {/* Drawer toggles — visible only below xl */}
            <button
              onClick={() => setOpenPanel('slides')}
              className="xl:hidden absolute left-3 top-3 z-20 p-2 rounded-lg bg-neutral-900/80 backdrop-blur border border-white/10 text-neutral-300 hover:text-white hover:bg-neutral-800 transition-colors"
              aria-label="Abrir painel de slides"
              aria-expanded={openPanel === 'slides'}
            >
              <PanelLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setOpenPanel('props')}
              className="xl:hidden absolute right-3 top-3 z-20 p-2 rounded-lg bg-neutral-900/80 backdrop-blur border border-white/10 text-neutral-300 hover:text-white hover:bg-neutral-800 transition-colors"
              aria-label="Abrir propriedades"
              aria-expanded={openPanel === 'props'}
            >
              <PanelRight className="w-4 h-4" />
            </button>

            {/* Drawer overlay — below xl only */}
            {openPanel && (
              <div
                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-30 xl:hidden"
                onClick={() => setOpenPanel(null)}
                aria-hidden="true"
              />
            )}

            {/* Left: Slide Panel — static column at xl+, drawer below */}
            <div
              className={`flex fixed inset-y-0 left-0 z-40 transition-transform duration-300 xl:static xl:z-auto xl:translate-x-0 xl:transition-none ${
                openPanel === 'slides' ? 'translate-x-0' : '-translate-x-full'
              }`}
            >
              <SlidePanel
                fields={editor.fields}
                pages={editor.pages}
                selectedId={editor.selectedFieldId}
                onSelect={(fieldId) => { editor.selectField(fieldId); setOpenPanel(null); }}
                onReorder={editor.reorderFields}
                onDelete={editor.deleteField}
                onDuplicate={editor.duplicateField}
                onAddPage={() => editor.addPage()}
                onDeletePage={editor.deletePage}
                onUpdatePage={editor.updatePage}
                onReorderPages={editor.reorderPages}
                onMoveFieldToPage={editor.moveFieldToPage}
                selectedPageId={editor.selectedPageId}
                onSelectPage={editor.selectPage}
              />
            </div>

            {/* Center: Preview + Toolbar */}
            <SlidePreview
              field={editor.selectedField}
              themeConfig={editor.themeConfig}
              viewportMode={editor.viewportMode}
              isPlayMode={editor.isPlayMode}
              form={editor.formSnapshot}
            >
              <SlideToolbar
                onToggleTheme={editor.toggleThemePanel}
                onToggleViewport={editor.toggleViewport}
                onPlay={editor.togglePlayMode}
                onUndo={editor.undo}
                onRedo={editor.redo}
                canUndo={editor.canUndo}
                canRedo={editor.canRedo}
                onSettings={editor.openSlideSettings}
                onImportMd={() => setShowMarkdownImporter(true)}
                onExportMd={editor.handleExportMarkdown}
                onCopyMd={editor.handleCopyMarkdown}
                viewportMode={editor.viewportMode}
                isPlayMode={editor.isPlayMode}
              />
            </SlidePreview>

            {/* Right: Properties Panel — static column at xl+, drawer below */}
            <div
              className={`flex fixed inset-y-0 right-0 z-40 transition-transform duration-300 bg-neutral-950/95 xl:bg-transparent xl:static xl:z-auto xl:translate-x-0 xl:transition-none ${
                openPanel === 'props' ? 'translate-x-0' : 'translate-x-full'
              }`}
            >
              {!editor.selectedField && !editor.selectedPage && (
                <p className="xl:hidden w-72 p-4 text-sm text-neutral-500">
                  Selecione um campo ou página para editar as propriedades.
                </p>
              )}
              <SlidePropertiesPanel
                field={editor.selectedField}
                allFields={editor.fields}
                onUpdate={editor.updateField}
                onUpdateConfig={editor.updateFieldConfig}
                onClose={() => { editor.selectField(null); editor.selectPage(null); setOpenPanel(null); }}
                scoringConfig={editor.settings?.scoring}
                onUpdateOptionScore={editor.updateOptionScore}
                onUpdateRules={editor.updateFieldRules}
                formSettings={editor.settings}
                onUpdateSettings={editor.updateSettings}
                userId={editor.user?.id}
                formId={id}
                selectedPage={editor.selectedPage}
                onUpdatePage={editor.updatePage}
                pages={editor.pages}
                onSetAlignmentAll={editor.setAlignmentAll}
              />
            </div>
          </>
        )}

        {activeTab === 'workflow' && (
          <div className="flex-1 min-h-0">
            <FlowEditor
              fields={editor.fields}
              onFieldsChange={(newFields) => {
                const currentById = new Map(editor.fields.map(f => [f.id, f]));
                newFields.forEach((field) => {
                  const current = currentById.get(field.id);
                  if (current && JSON.stringify(current) !== JSON.stringify(field)) {
                    editor.updateField(field.id, field);
                  }
                });
              }}
              scoringVariables={editor.settings?.scoring?.variables as any}
              pages={editor.pages}
              className="h-full"
            />
          </div>
        )}

        {activeTab === 'share' && id && (
          <div className="flex-1 min-h-0 overflow-y-auto">
            <div className="p-6 lg:p-8">
              <FormShare embedded />
            </div>
          </div>
        )}

        {activeTab === 'responses' && id && (
          <div className="flex-1 min-h-0 overflow-y-auto">
            <div className="p-6 lg:p-8">
              <FormResponses embedded />
            </div>
          </div>
        )}
      </div>

      {/* ─── Modals (via Portal to escape liquid-glass stacking) ─── */}

      {/* Unsaved changes blocker */}
      {editor.blocker.state === 'blocked' && createPortal(
        <Modal
          isOpen
          onClose={() => editor.blocker.reset?.()}
          title="Alterações não salvas"
        >
          <div className="space-y-4">
            <p className="text-neutral-300">
              Você tem alterações não salvas. Deseja sair sem salvar?
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => editor.blocker.reset?.()}
                className="px-4 py-2 rounded-lg bg-white/10 text-white hover:bg-white/15 transition-colors"
              >
                Continuar editando
              </button>
              <button
                onClick={() => editor.blocker.proceed?.()}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-500 transition-colors"
              >
                Sair sem salvar
              </button>
            </div>
          </div>
        </Modal>,
        document.body,
      )}

      {/* Theme Modal */}
      {createPortal(
        <Modal
          isOpen={editor.showThemePanel}
          onClose={editor.toggleThemePanel}
          title="Personalizar tema"
        >
          <ThemeSettings
            themeConfig={editor.themeConfig}
            onUpdate={(config) => editor.updateSettings({ themeConfig: config })}
          />
        </Modal>,
        document.body,
      )}

      {/* Add Field Modal */}
      <AddFieldModal
        isOpen={editor.showAddFieldModal}
        onClose={editor.closeAddFieldModal}
        onSelect={editor.addField}
        onImportBatch={editor.addFieldsBatch}
      />

      {/* Markdown Importer */}
      {createPortal(
        <MarkdownImporter
          isOpen={showMarkdownImporter}
          onClose={() => setShowMarkdownImporter(false)}
          onImport={editor.handleMarkdownImport}
          hasExistingFields={editor.fields.length > 0}
        />,
        document.body,
      )}

      {/* Settings Modal (Tabbed) */}
      {createPortal(
        <Modal
          isOpen={editor.showSettingsModal}
          onClose={editor.closeSlideSettings}
          title="Configurações do formulário"
          size="xl"
        >
          <Tabs.Root defaultValue="general">
            <Tabs.List className="mb-6">
              <Tabs.Trigger value="general">Geral</Tabs.Trigger>
              <Tabs.Trigger value="scoring">Pontuação</Tabs.Trigger>
              <Tabs.Trigger value="integrations">Integrações</Tabs.Trigger>
            </Tabs.List>

            <Tabs.Content value="general">
              <div className="space-y-6">
                <Select
                  label="Modo de exibição"
                  value={localDisplayMode}
                  onChange={(e) => editor.updateSettings({ displayMode: e.target.value as 'classic' | 'typeform' })}
                  options={[
                    { value: 'classic', label: 'Clássico' },
                    { value: 'typeform', label: 'Multi-Step Form' },
                  ]}
                  helpText={localDisplayMode === 'classic' ? 'Multi-step por blocos' : 'Uma pergunta por tela'}
                />

                <Checkbox
                  label="Mostrar barra de progresso"
                  checked={editor.settings?.showProgressBar !== false}
                  onChange={(e) => editor.updateSettings({ showProgressBar: e.target.checked })}
                />

                <Checkbox
                  label="Mostrar branding OneForm"
                  checked={editor.settings?.showBranding !== false}
                  onChange={(e) => editor.updateSettings({ showBranding: e.target.checked })}
                />

                <Input
                  label="Texto do botão de envio"
                  value={editor.settings?.submitButtonText || 'Enviar'}
                  onChange={(e) => editor.updateSettings({ submitButtonText: e.target.value })}
                />

                <Input
                  label="Texto do botão de próxima pergunta"
                  value={editor.settings?.nextButtonText || 'OK'}
                  onChange={(e) => editor.updateSettings({ nextButtonText: e.target.value })}
                />

                <div>
                  <Input
                    label="URL de redirecionamento após envio"
                    value={editor.settings?.redirectUrl ?? ''}
                    onChange={(e) => editor.updateSettings({ redirectUrl: e.target.value })}
                    placeholder="https://exemplo.com/obrigado"
                    helpText="Opcional. Se preenchido, o respondente é redirecionado automaticamente após enviar o formulário."
                  />
                  {editor.settings?.redirectUrl && !/^https?:\/\//.test(editor.settings.redirectUrl) && (
                    <p className="text-xs text-red-400 mt-1">URL deve começar com http:// ou https://</p>
                  )}
                </div>

                {/* Notificações por email (Story saas-1.13) */}
                <div className="border-t border-neutral-800 pt-4 mt-2">
                  <p className="text-sm font-medium text-neutral-300 mb-3">Notificações por email</p>
                  <div className="space-y-4">
                    <Checkbox
                      label="Avisar por email a cada nova resposta"
                      helpText="Um email é enviado ao receber resposta completa, com link pra visualizar no dashboard."
                      checked={editor.settings?.notifyOnSubmit === true}
                      onChange={(e) => {
                        // Gate só no enable: permitir desativar sem bloqueio
                        if (e.target.checked) {
                          if (
                            !ensureFeature(
                              'email_notifications',
                              'pro',
                              'Notificações por email estão disponíveis nos planos Pro e Business.'
                            )
                          ) {
                            return;
                          }
                        }
                        editor.updateSettings({ notifyOnSubmit: e.target.checked });
                      }}
                    />
                    {editor.settings?.notifyOnSubmit && (
                      <Input
                        label="Email de destino"
                        type="email"
                        value={editor.settings?.notifyEmail ?? ''}
                        onChange={(e) => editor.updateSettings({ notifyEmail: e.target.value })}
                        placeholder={editor.user?.email ?? 'seu@email.com'}
                        helpText="Se vazio, usa o email da sua conta."
                      />
                    )}
                  </div>
                </div>

                {/* Salvamento Parcial (Story 5.1) */}
                <div className="border-t border-neutral-800 pt-4 mt-2">
                  <p className="text-sm font-medium text-neutral-300 mb-3">Salvamento Parcial</p>
                  <div className="space-y-4">
                    <Checkbox
                      label="Permitir salvar progresso"
                      helpText="Respondentes podem sair e continuar depois no mesmo navegador"
                      checked={editor.settings?.partialSubmit?.enabled === true}
                      onChange={(e) => editor.updateSettings({
                        partialSubmit: {
                          ...editor.settings?.partialSubmit,
                          enabled: e.target.checked,
                          cookieExpiryDays: editor.settings?.partialSubmit?.cookieExpiryDays ?? 30,
                        },
                      })}
                    />
                    {editor.settings?.partialSubmit?.enabled && (
                      <>
                        <Input
                          label="Expiração do cookie (dias)"
                          type="number"
                          value={String(editor.settings?.partialSubmit?.cookieExpiryDays ?? 30)}
                          onChange={(e) => {
                            const days = Math.max(1, Math.min(365, Number(e.target.value) || 30));
                            editor.updateSettings({
                              partialSubmit: {
                                ...editor.settings?.partialSubmit,
                                enabled: true,
                                cookieExpiryDays: days,
                              },
                            });
                          }}
                          helpText="Quantos dias o navegador lembra o progresso (1-365)"
                        />
                        <Select
                          label="Notificações de salvamento parcial"
                          value={editor.settings?.partialSubmit?.notifyMode ?? 'complete_only'}
                          onChange={(e) => editor.updateSettings({
                            partialSubmit: {
                              ...editor.settings?.partialSubmit,
                              enabled: true,
                              notifyMode: e.target.value as 'none' | 'partial_only' | 'complete_only' | 'all',
                            },
                          })}
                          options={[
                            { value: 'none', label: 'Nenhuma' },
                            { value: 'partial_only', label: 'Só parciais' },
                            { value: 'complete_only', label: 'Só completas' },
                            { value: 'all', label: 'Todas' },
                          ]}
                          helpText="Quando receber notificações de respostas"
                        />
                      </>
                    )}
                  </div>
                </div>
              </div>
            </Tabs.Content>

            <Tabs.Content value="scoring">
              <ScoringSettings
                scoringConfig={editor.settings?.scoring || { enabled: false, variables: [], showResultToRespondent: false }}
                onUpdate={(config) => editor.updateSettings({ scoring: config })}
                fields={editor.fields}
              />
            </Tabs.Content>

            <Tabs.Content value="integrations">
              {id ? (
                <FormWebhooks
                  formId={id}
                  fields={editor.fields}
                  scoringEnabled={!!editor.settings?.scoring?.enabled}
                />
              ) : (
                <p className="text-neutral-500 text-sm">Salve o formulário primeiro para configurar webhooks.</p>
              )}
            </Tabs.Content>
          </Tabs.Root>
        </Modal>,
        document.body,
      )}
    </div>
  );
};
